import { assoc, isNil } from 'ramda';
import { CURRENCY_PLACEHOLDER } from '@enmon/commons/lib/enmon';
import { SCOPE_AND_STREAM_CONFIG } from '@/routes/carbon_footprint/routes/emissions/constants';
import { getTimeRanges } from './utils/getTimeRanges';
import { isUnit } from '@enmon/commons';

// trim if string
const trimIfString = value => (typeof value === 'string' ? value.trim() : value);

/**
 * Joins the given array into a string with the "," separator.
 * @param value value of the filter
 * @returns joined string or original value
 */
const joinIfArray = value => (value && Array.isArray(value) ? trimIfString(value).join(',') : trimIfString(value));

export const getQuery = params => {
  const {
    page,
    pageSize,
    split,
    tags,
    commodities,
    placements,
    meterKinds,
    types,
    granularity,
    period,
    customers,
    collections,
    supplyPoints,
    establishments,
    supplyPointCategories,
    supplyPointStatuses,
    notificationStatuses,
    leaseUnits,
    sorts,
    notifications,
    sources,
    issueTypes,
    search,
    userType,
    emissionRecords,
    emissionFactors,
    customerEmissionFactors,
    customEmissionFactorValidities,
    meters,
    meterRegisters,
    invoices,
    manualReadings,
    thirdPartyTypes,
    view,
    scopeAndStream,
    unit,
    previous,
    thirdParties,
    populate,
    severity,
    notificationType,
    notificationSettingsStatuses,
    createdBy,
    ...others
  } = params;

  const { scope: emissionScope, stream: emissionStream } = scopeAndStream
    ? SCOPE_AND_STREAM_CONFIG[scopeAndStream]
    : {};

  return Object.entries({
    ...others,
    search: search?.trim(),
    view,
    page,
    pageSize,
    tags:
      tags && Array.isArray(tags)
        ? tags.map(tag => (tag !== null && typeof tag === 'object' ? tag.key : tag)).join(',')
        : tags,
    ...((isUnit(unit) || unit === CURRENCY_PLACEHOLDER) && { unit }),
    ...(split ? { split: joinIfArray(split) } : {}),
    ...(types ? { types: types.join(',') } : {}),
    ...(scopeAndStream ? { emissionScope, emissionStream } : {}),
    commodities: joinIfArray(commodities),
    placements: joinIfArray(placements),
    meterKinds: joinIfArray(meterKinds),
    establishments: joinIfArray(establishments),
    supplyPointCategories: joinIfArray(supplyPointCategories),
    supplyPointStatuses: joinIfArray(supplyPointStatuses),
    notificationStatuses: joinIfArray(notificationStatuses),
    notifications: joinIfArray(notifications),
    leaseUnits: joinIfArray(leaseUnits),
    sorts: sorts?.map(({ field, order }) => `${order === 'descend' ? '-' : ''}${field}`).join(','),
    sources: joinIfArray(sources),
    issueTypes: joinIfArray(issueTypes),
    supplyPoints:
      supplyPoints && Array.isArray(supplyPoints)
        ? supplyPoints
            .map(supplyPoint =>
              supplyPoint !== null && typeof supplyPoint === 'object' ? supplyPoint.key : supplyPoint,
            )
            .join(',')
        : supplyPoints,
    meters: joinIfArray(meters),
    meterRegisters: meterRegisters
      ?.map(meterRegister =>
        typeof meterRegister === 'string'
          ? meterRegister
          : `${meterRegister.medium ?? '*'}-${meterRegister.obis ?? '*.*'}.${meterRegister.tariff ?? '*'}`,
      )
      .join(','),
    thirdPartyTypes,
    userType: userType === 'any' ? null : userType,
    customers: joinIfArray(customers),
    collections: joinIfArray(collections),
    invoices: joinIfArray(invoices),
    manualReadings: joinIfArray(manualReadings),
    emissionRecords: joinIfArray(emissionRecords),
    emissionFactors,
    customerEmissionFactors: joinIfArray(customerEmissionFactors),
    customEmissionFactorValidities: joinIfArray(customEmissionFactorValidities),
    previous,
    populate: joinIfArray(populate),
    thirdParties: joinIfArray(thirdParties),
    billingStatuses: joinIfArray(params.billingStatuses),
    severities: joinIfArray(severity),
    notificationTypes: joinIfArray(notificationType),
    notificationSettingsStatuses: joinIfArray(notificationSettingsStatuses),
    createdBy: joinIfArray(createdBy),
    ...(period ? { ...getTimeRanges(period, previous), granularity } : {}),
    ...(populate && { populate: joinIfArray(populate) }),
  })
    .filter(entry => !isNil(entry[1]))
    .reduce((acc, entry) => assoc(entry[0], entry[1], acc), {});
};
